import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import PageHeader from "../../components/common/PageHeader";
import FormField from "../../components/common/FormField";
import PageLoader from "../../components/common/PageLoader";
import useAuth from "../../hooks/useAuth";
import useFormValidation from "../../hooks/useFormValidation";
import { email, mobile, required, sanitizeNationalPhone } from "../../utils/formValidation";
import { accountService } from "../../services/account.service";
import { getApiErrorMessage } from "../../helpers";
import { AUTH_USER_TYPES } from "../../constants/auth.constants";
import { useDispatch } from "react-redux";
import { appActions } from "../../redux/actions";
import { authenticationTypes } from "../../redux/types";
import { pickUserId, saveAuthSession } from "../../helpers/authStorage.helper";
import { systemRoleService } from "../../modules/role-management/services/systemRole.service";
import { tenantRoleService } from "../../modules/role-management/services/tenantRole.service";
import { normalizeRoleList } from "../../modules/role-management/utils/normalizeRole";

const schema = {
  firstname: [required("First name is required.")],
  lastname: [],
  email: [email()],
  mobile: [mobile()],
};

const toNameList = (value) => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) =>
      typeof item === "string" ? item : item?.name || item?.role || item?.code || ""
    )
    .filter(Boolean);
};

const pickProfile = (source = {}) => ({
  firstname: source.firstname || source.firstName || "",
  lastname: source.lastname || source.lastName || "",
  email: source.email || "",
  mobile: sanitizeNationalPhone(
    source.mobile || source.mobileNumber || source.phone || source.phoneNumber || ""
  ),
  username: source.username || source.userName || "",
  userId: pickUserId(source),
  roles: toNameList(source.roles),
  permissions: toNameList(source.permissions?.length ? source.permissions : source.allow),
});

const resolvePermissionsFromRoles = async ({
  roles = [],
  userType,
  tenantId,
}) => {
  const roleNames = toNameList(roles);
  if (!roleNames.length) return [];

  // One list call instead of get-by-name per role.
  const payload =
    userType === AUTH_USER_TYPES.TENANT
      ? await tenantRoleService.getAllTenantRoles(tenantId)
      : await systemRoleService.getAllSystemRoles();
  const allRoles = normalizeRoleList(payload);
  const wanted = new Set(roleNames.map((name) => name.toLowerCase()));

  const permissions = new Set();
  allRoles.forEach((role) => {
    if (!wanted.has(String(role.name || "").toLowerCase())) return;
    (role.allow || []).forEach((item) => {
      if (item) permissions.add(String(item));
    });
  });

  return Array.from(permissions).sort();
};

const ProfilePage = () => {
  const dispatch = useDispatch();
  const auth = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [profileMeta, setProfileMeta] = useState(pickProfile(auth.user));

  const initialValues = useMemo(
    () => ({
      firstname: profileMeta.firstname,
      lastname: profileMeta.lastname,
      email: profileMeta.email,
      mobile: profileMeta.mobile,
    }),
    [profileMeta]
  );

  const { values, getFieldProps, validateAll, reset, setFieldValue } =
    useFormValidation(initialValues, schema);

  useEffect(() => {
    reset(initialValues);
  }, [initialValues, reset]);

  const persistUser = (nextUser, nextRoles, nextPermissions) => {
    dispatch({
      type: authenticationTypes.SET_AUTH_CONTEXT,
      user: nextUser,
      roles: nextRoles,
      permissions: nextPermissions,
    });
    saveAuthSession({
      user: nextUser,
      userType: auth.userType,
      tenantId: auth.tenantId,
      activeTenantId: auth.activeTenantId,
      activeTenantName: auth.activeTenantName,
      roles: nextRoles,
      permissions: nextPermissions,
    });
  };

  useEffect(() => {
    let cancelled = false;

    const loadProfile = async () => {
      setLoading(true);
      setError("");
      const sessionUser = auth.user || {};
      try {
        const data =
          auth.userType === AUTH_USER_TYPES.TENANT
            ? await accountService.getTenantProfile(pickUserId(sessionUser))
            : await accountService.getSystemProfile();
        if (cancelled) return;

        const nextMeta = pickProfile({ ...sessionUser, ...(data || {}) });
        const roles = nextMeta.roles.length ? nextMeta.roles : toNameList(auth.roles);

        let permissions = nextMeta.permissions.length
          ? nextMeta.permissions
          : toNameList(auth.permissions);

        // Profile/login often returns roles only — resolve allow list from each role.
        if (!permissions.length && roles.length) {
          permissions = await resolvePermissionsFromRoles({
            roles,
            userType: auth.userType,
            tenantId: auth.activeTenantId || auth.tenantId,
          });
        }

        if (cancelled) return;

        const enrichedMeta = { ...nextMeta, roles, permissions };
        setProfileMeta(enrichedMeta);
        persistUser(
          { ...sessionUser, ...(data || {}), ...enrichedMeta },
          roles,
          permissions
        );
      } catch (err) {
        if (!cancelled) {
          const fallback = pickProfile(sessionUser);
          const roles = fallback.roles.length
            ? fallback.roles
            : toNameList(auth.roles);
          let permissions = fallback.permissions.length
            ? fallback.permissions
            : toNameList(auth.permissions);
          if (!permissions.length && roles.length) {
            try {
              permissions = await resolvePermissionsFromRoles({
                roles,
                userType: auth.userType,
                tenantId: auth.activeTenantId || auth.tenantId,
              });
            } catch {
              permissions = [];
            }
          }
          setProfileMeta({ ...fallback, roles, permissions });
          const message = getApiErrorMessage(err);
          if (message && !String(message).toLowerCase().includes("404")) {
            setError(message);
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadProfile();
    return () => {
      cancelled = true;
    };
    // Load once per login context. Session fields are read from auth at call time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.userType, auth.tenantId]);

  const handleSave = async (event) => {
    event.preventDefault();
    const { isValid } = validateAll();
    if (!isValid) {
      setError("Please correct the highlighted fields.");
      return;
    }

    setSaving(true);
    setError("");
    const nextMobile = sanitizeNationalPhone(values.mobile);
    const originalMobile = sanitizeNationalPhone(profileMeta.mobile);
    const payload = {
      firstname: values.firstname.trim(),
      lastname: values.lastname.trim(),
      email: values.email.trim(),
      username: profileMeta.username,
    };
    // Only send mobile when it actually changed. Resending a stored
    // country-code value like 9180754909 returns "Invalid mobileNumber".
    if (nextMobile && nextMobile !== originalMobile) {
      payload.mobile = nextMobile;
    }

    try {
      const updated =
        auth.userType === AUTH_USER_TYPES.TENANT
          ? await accountService.updateTenantProfile(payload, profileMeta.userId)
          : await accountService.updateSystemProfile(payload);

      const nextUser = { ...auth.user, ...payload, ...(updated || {}) };
      const nextMeta = pickProfile(nextUser);
      persistUser(
        nextUser,
        nextMeta.roles.length ? nextMeta.roles : auth.roles,
        nextMeta.permissions.length ? nextMeta.permissions : auth.permissions
      );
      setProfileMeta({
        ...nextMeta,
        roles: nextMeta.roles.length ? nextMeta.roles : profileMeta.roles,
        permissions: nextMeta.permissions.length
          ? nextMeta.permissions
          : profileMeta.permissions,
      });
      dispatch(appActions.apiSuccessMessage("Profile updated successfully."));
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <PageLoader text="Loading profile..." />;
  }

  const roleList = profileMeta.roles.length ? profileMeta.roles : auth.roles;
  const permissionList = profileMeta.permissions.length
    ? profileMeta.permissions
    : auth.permissions;

  return (
    <Box sx={{ width: "100%" }}>
      <PageHeader
        title="My Profile"
        subtitle="View and update your account details."
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Account
        </Typography>
        <Stack spacing={2} sx={{ mb: 2.5 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Username
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {profileMeta.username || "—"}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                User ID
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-all" }}>
                {profileMeta.userId || "—"}
              </Typography>
            </Box>
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Login type
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {auth.contextLabel}
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Tenant
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {auth.isTenantUser
                  ? `${auth.tenantLabel || auth.tenantId}${
                      auth.tenantId && auth.tenantLabel !== auth.tenantId
                        ? ` (${auth.tenantId})`
                        : ""
                    }`
                  : "—"}
              </Typography>
            </Box>
          </Stack>
        </Stack>

        <Box component="form" noValidate onSubmit={handleSave}>
          <Stack spacing={2.5}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5}>
              <FormField label="First Name" required {...getFieldProps("firstname")} />
              <FormField label="Last Name" {...getFieldProps("lastname")} />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2.5}>
              <FormField label="Email" type="email" {...getFieldProps("email")} />
              <FormField
                label="Mobile"
                {...getFieldProps("mobile")}
                onChange={(event) =>
                  setFieldValue("mobile", sanitizeNationalPhone(event.target.value))
                }
                inputProps={{ maxLength: 10, inputMode: "numeric" }}
              />
            </Stack>
            <Stack direction="row" justifyContent="flex-end">
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? "Updating..." : "Update Profile"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>

      <Paper sx={{ p: 2.5 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Roles & permissions
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 0.75 }}
        >
          Roles
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
          {roleList.length ? (
            roleList.map((role) => <Chip key={role} label={role} size="small" />)
          ) : (
            <Typography variant="body2" color="text.secondary">
              No roles assigned.
            </Typography>
          )}
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 0.75 }}
        >
          Permissions
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {permissionList.length ? (
            permissionList.map((permission) => (
              <Chip
                key={permission}
                label={permission}
                size="small"
                variant="outlined"
                color={
                  permission === "idp.sys.user.manage" ? "primary" : "default"
                }
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No permissions resolved from assigned roles.
            </Typography>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export default ProfilePage;
